import {
  AUTONOMY_LEVELS,
  type AutonomyLevel,
  type Me,
  REQUEST_CATEGORIES,
  type AdminSettings as Settings,
} from "@hoffman-support/contracts";
import { useEffect, useState } from "react";
import { ApiError } from "../api";
import { settingsAutonomy, settingsBoardHold, settingsGet } from "../triage-api";

const LEVEL_TEXT: Record<AutonomyLevel, string> = {
  L0: "L0: research and comment only, no board write",
  L1: "L1: draft; a person promotes (default)",
  L2: "L2: create or reuse cards autonomously",
  L3: "L3: L2 plus state and comment sync",
};

export function AdminSettings({ me }: { me: Me }) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [holdReason, setHoldReason] = useState("");
  const [notice, setNotice] = useState("");
  const isAdmin = me.user.roles.includes("admin");

  useEffect(() => {
    settingsGet()
      .then(setSettings)
      .catch(() => setNotice("Settings could not be loaded."));
  }, []);

  if (!settings) return <p role="status">{notice || "Loading…"}</p>;

  async function change(category: string, level: string) {
    try {
      setSettings(
        await settingsAutonomy(me, { category, level: level ? (level as AutonomyLevel) : null }),
      );
      setNotice(
        `${category} now runs at ${level || `the default (${settings?.autonomy.default})`}. Takes effect within seconds; no deployment.`,
      );
    } catch (error) {
      setNotice(error instanceof ApiError ? error.failure.message : "The change failed.");
    }
  }

  async function hold(active: boolean) {
    try {
      setSettings(
        await settingsBoardHold(me, active ? { active, reason: holdReason } : { active }),
      );
      setNotice(
        active
          ? "Board hold set: every write is held for a person until it is lifted."
          : "Board hold lifted: writes resume.",
      );
    } catch (error) {
      setNotice(error instanceof ApiError ? error.failure.message : "The change failed.");
    }
  }

  return (
    <section aria-labelledby="settings-heading">
      <h2 id="settings-heading">Board settings</h2>
      <p>
        Last changed {settings.updatedAt ? new Date(settings.updatedAt).toLocaleString() : "never"}
        {settings.updatedByDisplayName ? ` by ${settings.updatedByDisplayName}` : ""}.{" "}
        {isAdmin ? "" : "Read-only: admins change these."}
      </p>

      <section
        aria-labelledby="hold-heading"
        style={{
          border: `2px solid ${settings.boardHold.active ? "#a4000f" : "#ddd"}`,
          padding: "0.75rem",
          marginBottom: "1rem",
        }}
      >
        <h3 id="hold-heading">Board hold: {settings.boardHold.active ? "ACTIVE" : "off"}</h3>
        {settings.boardHold.active ? <p>Reason: {settings.boardHold.reason}</p> : null}
        {isAdmin ? (
          settings.boardHold.active ? (
            <button type="button" onClick={() => void hold(false)}>
              Lift the hold
            </button>
          ) : (
            <>
              <label htmlFor="hold-reason">Reason</label>
              <br />
              <input
                id="hold-reason"
                type="text"
                value={holdReason}
                onChange={(e) => setHoldReason(e.target.value)}
                style={{ width: "60%" }}
              />{" "}
              <button
                type="button"
                onClick={() => void hold(true)}
                disabled={holdReason.trim().length < 3}
              >
                Set the hold
              </button>
            </>
          )
        ) : null}
      </section>

      <section aria-labelledby="autonomy-heading">
        <h3 id="autonomy-heading">Autonomy per category (default {settings.autonomy.default})</h3>
        <table style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th scope="col" style={{ textAlign: "left" }}>
                Category
              </th>
              <th scope="col" style={{ textAlign: "left" }}>
                Level
              </th>
            </tr>
          </thead>
          <tbody>
            {REQUEST_CATEGORIES.map((c) => {
              const override = settings.autonomy.overrides[c.id];
              return (
                <tr key={c.id} style={{ borderTop: "1px solid #ddd" }}>
                  <td style={{ paddingRight: "1rem" }}>{c.label}</td>
                  <td>
                    {isAdmin ? (
                      <select
                        value={override ?? ""}
                        onChange={(e) => void change(c.id, e.target.value)}
                        aria-label={`Autonomy for ${c.label}`}
                      >
                        <option value="">Default ({settings.autonomy.default})</option>
                        {AUTONOMY_LEVELS.map((l) => (
                          <option key={l} value={l}>
                            {LEVEL_TEXT[l]}
                          </option>
                        ))}
                      </select>
                    ) : (
                      LEVEL_TEXT[override ?? settings.autonomy.default]
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
      <p role="status" aria-live="polite">
        {notice}
      </p>
    </section>
  );
}
